// Shared GSAP motion system for the marketing site. One registration point for
// every plugin the landing page uses, plus small helpers that keep every animation
// reading as one voice: snappy, with a slight overshoot — mechanical rather than
// soft, matching the hard-shadow civic system (see the .auth-enter/.rise keyframes
// in application.css, which this mirrors for anything GSAP-driven).
//
// GSAP tweens set inline styles directly, so they never fall under application.css's
// blanket `prefers-reduced-motion: reduce` rule (that rule only zeroes CSS
// animations/transitions). Every helper below checks `reducedMotion` itself and
// jumps straight to the end state instead, the same way HeroFlow already does.
//
// Deliberately NOT ScrollSmoother: global smooth-scroll hijacks window scrolling,
// which breaks the sticky header, the in-page anchor links (lib/scroll.js), native
// scrollIntoView, and leaks a transformed scroll container into other SPA routes.
// The layered-depth look comes from `parallax()` (plain ScrollTrigger) instead —
// same effect, native scroll intact.
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";
import { CustomEase } from "gsap/CustomEase";

gsap.registerPlugin(ScrollTrigger, SplitText, CustomEase);

// Mobile browsers resize the viewport mid-scroll as the address bar/toolbar
// collapses. Left at its default, ScrollTrigger treats that as a real resize and
// refreshes every trigger's start/end — which re-maps progress on whatever's
// currently scrubbing and shows up as a visible jump/stutter. Desktop never
// triggers this (its viewport doesn't resize during a scroll), so this is a
// mobile-only, zero-downside fix.
ScrollTrigger.config({ ignoreMobileResize: true });

export const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const coarsePointer = window.matchMedia("(pointer: coarse)").matches;

// The house ease: quick, with a touch of overshoot. GSAP does not parse CSS
// cubic-bezier(...) strings as named eases, so define the same curve natively.
export const EASE = CustomEase.create("pandig-house", "M0,0 C0.18,0.92 0.3,1.06 1,1");

// The house scrub: every scroll-driven timeline trails the scrollbar by this many
// seconds instead of hard-locking to it (scrub: true). Raw wheel input arrives in
// discrete ticks, so a locked scrub jumps in steps; the short catch-up smooths the
// ticks into one continuous motion without making the page feel detached from the
// scroll. FlowRail's 0.4 set the precedent — this is the shared value for the tall
// pinned sections.
export const SCRUB = 0.5;

// The opacity a scroll-scrubbed reveal starts FROM — dim, never invisible.
//
// A section's top edge is exactly where its scrub sits at zero, and that is also
// where a deep link, a pager jump, a fling or a restored scroll position can put
// you. Fading essential copy up from 0 means all of those land on a blank page, so
// the heading, the price, the trust notes and the answers start dim and resolve as
// you scroll instead of appearing out of nothing. It also means the page still reads
// if GSAP never runs at all: nothing but gsap.set() ever dims it. Decorative layers
// (rail pulses, the stacked cards behind the front one) may still start at 0 — no
// argument is lost when those are missing. LandingStakes's word-by-word reveal set this
// value; it's shared so every page dims by the same amount.
export const DIM = 0.22;

// Run `setup` inside a gsap.context() scoped to `root`. Returns a cleanup function
// that reverts every tween/ScrollTrigger the setup created — call it from
// onUnmounted so leaving the page (or a test remount) never leaks scroll listeners.
export function useMotion(root, setup) {
  if (!root) return () => {};
  const ctx = gsap.context(setup, root);
  return () => ctx.revert();
}

// Fade + rise a set of elements in once they enter the viewport, staggered — the
// scroll-triggered sibling of the .stagger CSS utility. Pass `eager: true` to play
// immediately instead of waiting on scroll (for above-the-fold content).
export function reveal(targets, { stagger = 0.08, y = 24, delay = 0, trigger, start = "top 85%", eager = false } = {}) {
  const els = gsap.utils.toArray(targets);
  if (!els.length) return null;

  if (reducedMotion) {
    gsap.set(els, { opacity: 1, y: 0 });
    return null;
  }

  gsap.set(els, { opacity: 0, y });
  const vars = { opacity: 1, y: 0, duration: 0.55, ease: EASE, stagger, delay };
  if (!eager) vars.scrollTrigger = { trigger: trigger ?? els[0], start, once: true };
  return gsap.to(els, vars);
}

// Split a headline into words parked below a mask, ready to rise into place one after
// another (`gsap.to(split.words, { opacity: 1, y: "0%", stagger })`). Both hero
// headlines open this way; the settings below are the reason it can be shared.
//
// A SplitText word mask is an `overflow: clip` box exactly one line-height tall. That
// suits text set loosely and cuts a hero to pieces: these headlines are set at 1.05
// while Poppins' glyphs span about 1.4em, so the tails of a g or a j overshoot the box
// by around 0.1em and land sliced flat — which readers take for broken rendering
// rather than tight leading. Padding the box past the descenders and pulling the same
// amount back off the margin makes the clip deeper without moving a single line (an
// `overflow: clip` inline-block sits on its bottom margin edge, which the negative
// margin puts back exactly where it was), and 130% clears the deepened box at both of
// the leadings in use (1.25em of box against 1.365em of travel at 1.05), so no word
// shows before it rises. The tail is twice the overshoot it has to cover, so a fallback
// font with deeper tails than Poppins still lands inside the box.
//
// Deliberately NOT reverted once the words have landed. Putting the original markup
// back re-lays out and re-rasterises every glyph a beat after the motion has stopped,
// and a headline that redraws itself in silence reads as the page loading it twice.
// The reveal's end state has to BE the resting state.
const MASK_TAIL = "0.2em";

export function splitHeadlineWords(el) {
  const split = SplitText.create(el, { type: "words", mask: "words" });
  gsap.set(split.masks, { paddingBottom: MASK_TAIL, marginBottom: `-${MASK_TAIL}` });
  gsap.set(split.words, { opacity: 0, y: "130%" });
  return split;
}

// Wind a straight rail back onto its own start point, ready to be drawn on, and hand
// back the x2 it draws out to: `gsap.to(line, { attr: { x2: tip } })`.
//
// Deliberately NOT the usual stroke-dasharray/stroke-dashoffset draw-on. Every rail
// on the site is a `vector-effect="non-scaling-stroke"` line inside a
// `preserveAspectRatio="none"` viewBox (that combination is what keeps a 2px ink
// stroke exactly 2px while the box stretches to whatever width it's given), and with
// non-scaling-stroke Chrome applies the dash pattern in DEVICE pixels while the
// geometry stays in viewBox units. No dash length is right in both spaces: measured
// in viewBox units it draws a stub, measured in CSS pixels it draws a rail that is
// whole on a 1× display and stops halfway on a 2× one — which is exactly how the
// hero rendered on Chrome and Edge (Safari, which dashes in user units, looked fine
// throughout, as did headless Chromium, which is why the suite never saw it).
// pathLength doesn't rescue it either: Chrome normalises against the user-unit length
// and then dashes in device pixels, so the rail comes out as a row of dashes.
//
// The end point needs no length at all. It is authored in the same coordinate space
// as the line itself, so every browser at every pixel ratio agrees on where it is.
export function collapseRail(line) {
  const tip = Number(line.getAttribute("x2"));
  gsap.set(line, { attr: { x2: line.getAttribute("x1") } });
  return tip;
}

// Depth without hijacking the scroll: an element drifts vertically as its section
// travels through the viewport, scrubbed to scroll position. `distance` is the total
// travel in px (negative = rises faster than the page → foreground; positive =
// lags → background). Decorative only; never wrap queried/interactive content.
export function parallax(el, { distance = -80, trigger, start = "top bottom", end = "bottom top" } = {}) {
  // Decorative-only elements are often `hidden md:block`/`hidden lg:block` — skip
  // wiring up a scrub tween for something that isn't even rendered (e.g. on
  // mobile), so it doesn't cost a scroll-linked recalculation for nothing.
  if (!el || reducedMotion || (el.offsetWidth === 0 && el.offsetHeight === 0)) return null;
  return gsap.fromTo(
    el,
    { y: distance / 2 },
    {
      y: -distance / 2,
      ease: "none",
      scrollTrigger: { trigger: trigger ?? el, start, end, scrub: true },
    },
  );
}

// Stamp an element into place: a rise + scale with an overshoot, like a rubber
// stamp landing on paper. Reserved for one moment — the final call-to-action — so
// the civic system gets a single playful beat right at the close of the page.
export function stampIn(targets, { trigger, start = "top 85%", delay = 0, y = 28, duration = 0.7 } = {}) {
  const els = gsap.utils.toArray(targets);
  if (!els.length) return null;

  if (reducedMotion) {
    gsap.set(els, { opacity: 1, y: 0, scale: 1 });
    return null;
  }

  gsap.set(els, { opacity: 0, y, scale: 0.94 });
  return gsap.to(els, {
    opacity: 1,
    y: 0,
    scale: 1,
    duration,
    delay,
    ease: "back.out(1.8)",
    scrollTrigger: { trigger: trigger ?? els[0], start, once: true },
  });
}

// Magnetic hover: nudges an element toward the cursor within `max` pixels, then
// snaps back with the house ease on release. Skipped for touch/coarse pointers and
// reduced motion, so it never fights the Button component's own tactile press
// interaction on devices where a hover concept doesn't apply. Returns a cleanup
// function — call it on unmount to drop the listeners.
export function magnetic(el, { strength = 0.35, max = 14 } = {}) {
  if (!el || reducedMotion || coarsePointer) return () => {};

  const onMove = (event) => {
    const rect = el.getBoundingClientRect();
    const relX = event.clientX - (rect.left + rect.width / 2);
    const relY = event.clientY - (rect.top + rect.height / 2);
    gsap.to(el, {
      x: gsap.utils.clamp(-max, max, relX * strength),
      y: gsap.utils.clamp(-max, max, relY * strength),
      duration: 0.35,
      ease: "power2.out",
    });
  };
  const onLeave = () => gsap.to(el, { x: 0, y: 0, duration: 0.5, ease: EASE });

  el.addEventListener("pointermove", onMove);
  el.addEventListener("pointerleave", onLeave);
  return () => {
    el.removeEventListener("pointermove", onMove);
    el.removeEventListener("pointerleave", onLeave);
  };
}

// Vue directive wrapper for `magnetic`. Apply to a small inline-block <span> AROUND
// a Button rather than the Button itself, so the magnetic pull (an inline transform
// on the wrapper) never overwrites the Button's own CSS hover-lift / active-press
// transform. Usage: `import { vMagnetic } from "@/lib/motion"` then `v-magnetic`.
export const vMagnetic = {
  mounted(el, binding) {
    el.__magnetic = magnetic(el, binding.value || {});
  },
  unmounted(el) {
    el.__magnetic?.();
  },
};

export { gsap, ScrollTrigger, SplitText };
